import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import Input from '../ui/Input';

const FeedbackModal = ({ show, onClose, onSubmit, interview, loading = false }) => {
  const [formData, setFormData] = useState({
    feedback: '',
    rating: 3,
    technical_skills: 3,
    communication_skills: 3,
    cultural_fit: 3,
    strengths: '',
    areas_for_improvement: '',
    recommendation: 'consider'
  });

  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.feedback.trim()) {
      setError('Feedback is required');
      return;
    }

    if (formData.rating < 1 || formData.rating > 5) {
      setError('Please provide a valid rating between 1 and 5');
      return;
    }

    try {
      await onSubmit(formData);
      handleClose();
    } catch (error) {
      setError(error.message);
    }
  };

  const handleClose = () => {
    setFormData({
      feedback: '',
      rating: 3,
      technical_skills: 3,
      communication_skills: 3,
      cultural_fit: 3,
      strengths: '',
      areas_for_improvement: '',
      recommendation: 'consider'
    });
    setError(null);
    onClose();
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-surface rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-text-primary">Complete Interview</h3>
            <p className="text-text-secondary text-sm">
              Provide feedback for {interview?.candidate_name}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-secondary-100 rounded-lg"
            disabled={loading}
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Overall Rating */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Overall Rating *
            </label>
            <div className="flex items-center space-x-4">
              {[1, 2, 3, 4, 5].map((rating) => (
                <button
                  key={rating}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, rating }))}
                  className={`p-2 rounded-lg border transition-colors ${
                    formData.rating >= rating
                      ? 'bg-yellow-100 border-yellow-300 text-yellow-700'
                      : 'bg-white border-border text-text-muted hover:border-yellow-300'
                  }`}
                >
                  <Icon name="Star" size={24} />
                </button>
              ))}
              <span className="text-text-secondary ml-2">
                ({formData.rating}/5)
              </span>
            </div>
          </div>

          {/* Skill Ratings */}
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Technical Skills
              </label>
              <select
                value={formData.technical_skills}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  technical_skills: parseInt(e.target.value) 
                }))}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value={1}>1 - Poor</option>
                <option value={2}>2 - Below Average</option>
                <option value={3}>3 - Average</option>
                <option value={4}>4 - Good</option>
                <option value={5}>5 - Excellent</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Communication
              </label>
              <select
                value={formData.communication_skills}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  communication_skills: parseInt(e.target.value) 
                }))}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value={1}>1 - Poor</option>
                <option value={2}>2 - Below Average</option>
                <option value={3}>3 - Average</option>
                <option value={4}>4 - Good</option>
                <option value={5}>5 - Excellent</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Cultural Fit
              </label>
              <select
                value={formData.cultural_fit}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  cultural_fit: parseInt(e.target.value) 
                }))}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value={1}>1 - Poor</option>
                <option value={2}>2 - Below Average</option>
                <option value={3}>3 - Average</option>
                <option value={4}>4 - Good</option>
                <option value={5}>5 - Excellent</option>
              </select>
            </div>
          </div>

          {/* Detailed Feedback */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Overall Feedback *
            </label>
            <textarea
              value={formData.feedback}
              onChange={(e) => setFormData(prev => ({ ...prev, feedback: e.target.value }))}
              placeholder="Provide detailed feedback about the candidate's performance, answers, and overall impression..."
              rows={4}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none"
              required
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Key Strengths
              </label>
              <textarea
                value={formData.strengths}
                onChange={(e) => setFormData(prev => ({ ...prev, strengths: e.target.value }))}
                placeholder="What are the candidate's main strengths?"
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Areas for Improvement
              </label>
              <textarea
                value={formData.areas_for_improvement}
                onChange={(e) => setFormData(prev => ({ ...prev, areas_for_improvement: e.target.value }))}
                placeholder="What areas could the candidate improve on?"
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none"
              />
            </div>
          </div>

          {/* Recommendation */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Recommendation
            </label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'hire', label: 'Hire', color: 'green', icon: 'CheckCircle' },
                { value: 'consider', label: 'Consider', color: 'yellow', icon: 'Clock' },
                { value: 'reject', label: 'Reject', color: 'red', icon: 'XCircle' }
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, recommendation: option.value }))}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    formData.recommendation === option.value
                      ? `border-${option.color}-500 bg-${option.color}-50 text-${option.color}-700`
                      : 'border-border bg-white text-text-secondary hover:border-secondary-300'
                  }`}
                >
                  <div className="flex flex-col items-center space-y-1">
                    <Icon 
                      name={option.icon} 
                      size={20} 
                      className={formData.recommendation === option.value ? `text-${option.color}-600` : 'text-text-muted'} 
                    />
                    <span className="text-sm font-medium">{option.label}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              iconName="CheckCircle"
              loading={loading}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Complete Interview'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeedbackModal;
